"use client";

import React, { useState } from "react";
import { Plus, Edit, X, ChevronDown } from "lucide-react";
import Button from "./Masters/Button";
import RTE from "./RTE";

interface Day {
  id: number;
  dayNumber: number;
  cityName: string;
}

interface Sightseeing {
  id: string;
  name: string;
  duration: string;
  thumbnail: string;
  description: string;
  action: "add" | "option" | "none";
}

interface Flight {
  id: string;
  fromSector: string;
  toSector: string;
  airline: string;
  description: string;
}

const ItineraryInformation: React.FC = () => {
  const [selectedDay, setSelectedDay] = useState(1);
  const [selectedCity, setSelectedCity] = useState("");
  const [itineraryTitle, setItineraryTitle] = useState("");
  const [detailedItinerary, setDetailedItinerary] = useState("");
  
  // Hotel selections
  const [standardHotel, setStandardHotel] = useState("");
  const [standardRoomType, setStandardRoomType] = useState("");
  const [standardMealplan, setStandardMealplan] = useState("");
  const [deluxeHotel, setDeluxeHotel] = useState("");
  const [deluxeRoomType, setDeluxeRoomType] = useState("");
  const [deluxeMealplan, setDeluxeMealplan] = useState("");
  const [luxuryHotel, setLuxuryHotel] = useState("");
  const [luxuryRoomType, setLuxuryRoomType] = useState("");
  const [luxuryMealplan, setLuxuryMealplan] = useState("");

  // Transport options
  const [transportPointToPoint, setTransportPointToPoint] = useState(false);
  const [transportAtDisposal, setTransportAtDisposal] = useState(false);
  const [transportOptions, setTransportOptions] = useState({
    cab: false,
    sic: false,
    bus: false,
    train: false,
  });

  // Sightseeing options
  const [privateSightseeing, setPrivateSightseeing] = useState(false);
  const [sicSightseeing, setSicSightseeing] = useState(false);
  const [sightseeings, setSightseeings] = useState<Sightseeing[]>([
    {
      id: "1",
      name: "Lotus Temple",
      duration: "",
      thumbnail: "",
      description: "",
      action: "none",
    },
  ]);

  // Flight data
  const [flights, setFlights] = useState<Flight[]>([
    {
      id: "1",
      fromSector: "Delhi",
      toSector: "Jaipur",
      airline: "Indigo Air",
      description: "",
    },
  ]);
  const [newFlight, setNewFlight] = useState({
    fromSector: "",
    toSector: "",
    airline: "",
    description: "",
  });

  const days: Day[] = [
    { id: 1, dayNumber: 1, cityName: "Delhi" },
    { id: 2, dayNumber: 2, cityName: "city Name" },
    { id: 3, dayNumber: 3, cityName: "city Name" },
  ];

  const handleAddSightseeing = () => {
    setSightseeings([
      ...sightseeings,
      {
        id: Date.now().toString(),
        name: "",
        duration: "",
        thumbnail: "",
        description: "",
        action: "none",
      },
    ]);
  };

  const handleRemoveSightseeing = (id: string) => {
    setSightseeings(sightseeings.filter((s) => s.id !== id));
  };

  const handleAddFlight = () => {
    if (newFlight.fromSector && newFlight.toSector && newFlight.airline) {
      setFlights([
        ...flights,
        {
          id: Date.now().toString(),
          ...newFlight,
        },
      ]);
      setNewFlight({
        fromSector: "",
        toSector: "",
        airline: "",
        description: "",
      });
    }
  };

  const handleRemoveFlight = (id: string) => {
    setFlights(flights.filter((f) => f.id !== id));
  };

  return (
    <div className="flex gap-4">
      {/* Left Sidebar - Days */}
      <aside className="w-48 bg-white border border-gray-200 rounded-lg overflow-hidden text-xs">
        {days.map((day) => (
          <button
            key={day.id}
            type="button"
            onClick={() => setSelectedDay(day.id)}
            className={`w-full text-left px-3 py-2 border-b border-gray-100 hover:bg-blue-50 ${
              selectedDay === day.id
                ? "bg-blue-100 font-semibold"
                : "bg-white"
            }`}
          >
            Day {day.dayNumber} ({day.cityName})
          </button>
        ))}
        <button
          type="button"
          className="w-full text-left px-3 py-2 border-b border-gray-100 hover:bg-blue-50 bg-white text-blue-600"
        >
          + Add Day
        </button>
      </aside>

      {/* Main Content */}
      <div className="flex-1 space-y-4">
        {/* City Introduction & Itinerary Details */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-[11px] font-medium text-gray-700 mb-1">
                City Introduction
              </label>
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option>-- Select City --</option>
              </select>
            </div>
            <Button
              size="sm"
              variant="success"
              icon={<Plus className="w-4 h-4" />}
              iconPosition="left"
            >
              + Add City
            </Button>
          </div>

          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">
              Itinerary Title *
            </label>
            <input
              type="text"
              value={itineraryTitle}
              onChange={(e) => setItineraryTitle(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">
              Detailed Itinerary *
            </label>
            <RTE
              content={detailedItinerary}
              onChange={setDetailedItinerary}
            />
          </div>
        </div>

        {/* Select Hotel Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">
            Select Hotel
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
            {/* Standard */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-700">Standard</h4>
              <p className="text-[10px] text-gray-500">
                Add (Using Google or Manually)
              </p>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                  Add Hotel
                </label>
                <select
                  value={standardHotel}
                  onChange={(e) => setStandardHotel(e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option>-- Select Hotel --</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                  Room Type *
                </label>
                <select
                  value={standardRoomType}
                  onChange={(e) => setStandardRoomType(e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option>--Select--</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                  Mealplan Type
                </label>
                <select
                  value={standardMealplan}
                  onChange={(e) => setStandardMealplan(e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option>-- Select Mealplan --</option>
                </select>
              </div>
            </div>

            {/* Deluxe */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-700">Deluxe</h4>
              <p className="text-[10px] text-gray-500">
                Add (Using Google or Manually)
              </p>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                  Add Hotel
                </label>
                <select
                  value={deluxeHotel}
                  onChange={(e) => setDeluxeHotel(e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option>-- Select Hotel --</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                  Room Type
                </label>
                <select
                  value={deluxeRoomType}
                  onChange={(e) => setDeluxeRoomType(e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option>-- Select Room --</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                  Mealplan Type
                </label>
                <select
                  value={deluxeMealplan}
                  onChange={(e) => setDeluxeMealplan(e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option>-- Select Mealplan --</option>
                </select>
              </div>
            </div>

            {/* Luxury */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-700">Luxury</h4>
              <p className="text-[10px] text-gray-500">
                Add (Using Google or Manually)
              </p>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                  Add Hotel
                </label>
                <select
                  value={luxuryHotel}
                  onChange={(e) => setLuxuryHotel(e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option>-- Select Hotel --</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                  Room Type
                </label>
                <select
                  value={luxuryRoomType}
                  onChange={(e) => setLuxuryRoomType(e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option>-- Select Room --</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                  Mealplan Type
                </label>
                <select
                  value={luxuryMealplan}
                  onChange={(e) => setLuxuryMealplan(e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option>-- Select Mealplan --</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Transport Options */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">
            Transport Options
          </h3>
          <div className="space-y-4 text-xs">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={transportPointToPoint}
                  onChange={(e) => setTransportPointToPoint(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-[11px] font-medium text-gray-700">
                  Transport point to point
                </span>
              </label>
              {transportPointToPoint && (
                <div className="flex items-center gap-3 ml-4">
                  {["Cab", "SIC", "Bus", "Train"].map((option) => (
                    <label
                      key={option}
                      className="flex items-center gap-1 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={transportOptions[option.toLowerCase() as keyof typeof transportOptions]}
                        onChange={(e) =>
                          setTransportOptions({
                            ...transportOptions,
                            [option.toLowerCase()]: e.target.checked,
                          })
                        }
                        className="h-3 w-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-[11px] text-gray-600">{option}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={transportAtDisposal}
                  onChange={(e) => setTransportAtDisposal(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-[11px] font-medium text-gray-700">
                  Transport at Disposal
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Sightseeing Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">
            Sightseeing
          </h3>
          <div className="space-y-4 text-xs">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={privateSightseeing}
                  onChange={(e) => setPrivateSightseeing(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-[11px] font-medium text-gray-700">
                  Privet Sightseeing
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sicSightseeing}
                  onChange={(e) => setSicSightseeing(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-[11px] font-medium text-gray-700">
                  SIC Sightseeing
                </span>
              </label>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] text-gray-500">
                  Add (Using Google or Manually)
                </p>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={handleAddSightseeing}
                  icon={<Plus className="w-4 h-4" />}
                  iconPosition="left"
                >
                  Add Sightseeing Detail
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-xs border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left border-b">Sightseeing Name</th>
                      <th className="px-3 py-2 text-left border-b">Duration</th>
                      <th className="px-3 py-2 text-left border-b">Thamb</th>
                      <th className="px-3 py-2 text-left border-b">Description</th>
                      <th className="px-3 py-2 text-left border-b">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sightseeings.map((sightseeing) => (
                      <tr key={sightseeing.id} className="border-b">
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              className="h-3 w-3 rounded border-gray-300"
                            />
                            <input
                              type="text"
                              value={sightseeing.name}
                              onChange={(e) =>
                                setSightseeings(
                                  sightseeings.map((s) =>
                                    s.id === sightseeing.id
                                      ? { ...s, name: e.target.value }
                                      : s
                                  )
                                )
                              }
                              placeholder="Sightseeing Name"
                              className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs"
                            />
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={sightseeing.duration}
                            onChange={(e) =>
                              setSightseeings(
                                sightseeings.map((s) =>
                                  s.id === sightseeing.id
                                    ? { ...s, duration: e.target.value }
                                    : s
                                )
                              )
                            }
                            placeholder="Duration"
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              id={`thumb-${sightseeing.id}`}
                            />
                            <label
                              htmlFor={`thumb-${sightseeing.id}`}
                              className="px-2 py-1 border border-gray-300 rounded text-xs cursor-pointer hover:bg-gray-50"
                            >
                              Choose File
                            </label>
                            {sightseeing.thumbnail && (
                              <span className="text-xs text-gray-500">
                                {sightseeing.thumbnail.substring(0, 10)}...
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={sightseeing.description}
                            onChange={(e) =>
                              setSightseeings(
                                sightseeings.map((s) =>
                                  s.id === sightseeing.id
                                    ? { ...s, description: e.target.value }
                                    : s
                                )
                              )
                            }
                            placeholder="Description"
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="space-y-1">
                            <button
                              type="button"
                              className="flex items-center gap-1 px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                            >
                              Action <ChevronDown className="w-3 h-3" />
                            </button>
                            <div className="flex flex-col gap-1">
                              {["Add", "Option", "None"].map((action) => (
                                <label
                                  key={action}
                                  className="flex items-center gap-1 cursor-pointer"
                                >
                                  <input
                                    type="radio"
                                    name={`action-${sightseeing.id}`}
                                    checked={sightseeing.action === action.toLowerCase()}
                                    onChange={() =>
                                      setSightseeings(
                                        sightseeings.map((s) =>
                                          s.id === sightseeing.id
                                            ? {
                                                ...s,
                                                action: action.toLowerCase() as "add" | "option" | "none",
                                              }
                                            : s
                                        )
                                      )
                                    }
                                    className="h-3 w-3"
                                  />
                                  <span className="text-[10px] text-gray-600">
                                    {action}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Select Flight Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">
            Select Flight
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left border-b">From Sector</th>
                  <th className="px-3 py-2 text-left border-b">To Sector</th>
                  <th className="px-3 py-2 text-left border-b">Select Airline</th>
                  <th className="px-3 py-2 text-left border-b">Description</th>
                  <th className="px-3 py-2 text-left border-b">Action</th>
                </tr>
              </thead>
              <tbody>
                {flights.map((flight) => (
                  <tr key={flight.id} className="border-b">
                    <td className="px-3 py-2">{flight.fromSector}</td>
                    <td className="px-3 py-2">{flight.toSector}</td>
                    <td className="px-3 py-2">{flight.airline}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="text-blue-500 hover:text-blue-700"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveFlight(flight.id)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2"></td>
                  </tr>
                ))}
                {/* New Flight Input Rows */}
                <tr className="border-b">
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={newFlight.fromSector}
                      onChange={(e) =>
                        setNewFlight({ ...newFlight, fromSector: e.target.value })
                      }
                      placeholder="From Sector"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={newFlight.toSector}
                      onChange={(e) =>
                        setNewFlight({ ...newFlight, toSector: e.target.value })
                      }
                      placeholder="To Sector"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={newFlight.airline}
                      onChange={(e) =>
                        setNewFlight({ ...newFlight, airline: e.target.value })
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    >
                      <option>--Select Airline--</option>
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={newFlight.description}
                      onChange={(e) =>
                        setNewFlight({
                          ...newFlight,
                          description: e.target.value,
                        })
                      }
                      placeholder="Description"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Button
                      size="sm"
                      variant="success"
                      onClick={handleAddFlight}
                      icon={<Plus className="w-4 h-4" />}
                      iconPosition="left"
                    >
                      + Add
                    </Button>
                  </td>
                </tr>
                <tr>
                  <td className="px-3 py-2">
                    <select
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    >
                      <option>--Select Sector--</option>
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    >
                      <option>--Select Sector--</option>
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <select className="w-full border border-gray-300 rounded px-2 py-1 text-xs">
                      <option>--Select Airline--</option>
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      placeholder="Description"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Button
                      size="sm"
                      variant="success"
                      icon={<Plus className="w-4 h-4" />}
                      iconPosition="left"
                    >
                      + Add
                    </Button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItineraryInformation;

